import { FC, useState } from 'react'
import { Button, InputNumber, Space } from 'antd'
import { SaveOutlined, CloseOutlined } from '@ant-design/icons'
import { Formik, Form, Field, FieldProps } from 'formik'
import * as yup from 'yup'

// CSS for financial input styling and compact sizing
const financialInputStyles = `
  .financial-input .ant-input-number-input {
    text-align: right !important;
  }
  .compact-input-number .ant-input-number {
    font-size: 12px !important;
  }
  .compact-input-number .ant-input-number-input {
    padding: 2px 6px !important;
    height: 22px !important;
  }
  .compact-input-number .ant-input-number-handler-wrap {
    height: 22px !important;
  }
  .compact-button {
    padding: 2px 8px !important;
    font-size: 12px !important;
    height: 24px !important;
    line-height: 1.2 !important;
  }
`

interface IInlineDefaultPricingEditProps {
  initialValues: {
    largeIceCubePriceTHB: number
    smallIceCubePriceTHB: number
    crushedIcePriceTHB: number
  }
  onSave: (values: {
    largeIceCubePriceTHB: number
    smallIceCubePriceTHB: number
    crushedIcePriceTHB: number
  }) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

// Validation schema for default pricing (no name field)
const defaultPricingValidationSchema = yup.object({
  largeIceCubePriceTHB: yup
    .number()
    .transform((value, originalValue) => 
      originalValue === '' || originalValue === null || originalValue === undefined ? undefined : value
    )
    .required('Please enter large ice cube price')
    .min(0, 'Price cannot be negative')
    .max(1000000, 'Maximum price is 1,000,000 THB')
    .typeError('Please enter numbers only'),
  smallIceCubePriceTHB: yup
    .number()
    .transform((value, originalValue) => 
      originalValue === '' || originalValue === null || originalValue === undefined ? undefined : value
    )
    .required('Please enter small ice cube price')
    .min(0, 'Price cannot be negative')
    .max(1000000, 'Maximum price is 1,000,000 THB')
    .typeError('Please enter numbers only'),
  crushedIcePriceTHB: yup
    .number()
    .transform((value, originalValue) => 
      originalValue === '' || originalValue === null || originalValue === undefined ? undefined : value
    )
    .required('Please enter crushed ice price')
    .min(0, 'Price cannot be negative')
    .max(1000000, 'Maximum price is 1,000,000 THB')
    .typeError('Please enter numbers only'),
})

export const InlineDefaultPricingEdit: FC<IInlineDefaultPricingEditProps> = ({
  initialValues,
  onSave,
  onCancel,
  loading = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (values: typeof initialValues) => {
    setIsSubmitting(true)
    try {
      await onSave(values)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: financialInputStyles }} />
      <div className='bg-orange-50 p-3 rounded-lg border-2 border-orange-200'>
      <div className='mb-1.5'>
        <h3 className='text-sm font-medium text-orange-800'>
          Edit Default Customer Pricing
        </h3>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={defaultPricingValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, errors, touched, isValid }) => (
          <Form>
            <div className='grid grid-cols-3 gap-2 mb-3'>
              {/* Large Ice Price */}
              <div>
                <label className='block text-xs font-medium text-gray-700 mb-0.5'>
                  ใหญ่ *
                </label>
                <Field name='largeIceCubePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input compact-input-number'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.largeIceCubePriceTHB &&
                        touched.largeIceCubePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('largeIceCubePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.largeIceCubePriceTHB &&
                  touched.largeIceCubePriceTHB && (
                    <div className='text-red-500 text-xs mt-0.5'>
                      {errors.largeIceCubePriceTHB}
                    </div>
                  )}
              </div>

              {/* Small Ice Price */}
              <div>
                <label className='block text-xs font-medium text-gray-700 mb-0.5'>
                  เล็ก *
                </label>
                <Field name='smallIceCubePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input compact-input-number'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.smallIceCubePriceTHB &&
                        touched.smallIceCubePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('smallIceCubePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.smallIceCubePriceTHB &&
                  touched.smallIceCubePriceTHB && (
                    <div className='text-red-500 text-xs mt-0.5'>
                      {errors.smallIceCubePriceTHB}
                    </div>
                  )}
              </div>

              {/* Crushed Ice Price */}
              <div>
                <label className='block text-xs font-medium text-gray-700 mb-0.5'>
                  โม่ *
                </label>
                <Field name='crushedIcePriceTHB'>
                  {({ field }: FieldProps) => (
                    <InputNumber
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onBlur={field.onBlur}
                      placeholder='0.00'
                      min={0}
                      max={1000000}
                      step={0.01}
                      precision={2}
                      className='financial-input compact-input-number'
                      style={{ width: '100%' }}
                      size='small'
                      disabled={loading || isSubmitting}
                      status={
                        errors.crushedIcePriceTHB && touched.crushedIcePriceTHB
                          ? 'error'
                          : ''
                      }
                      addonBefore='฿'
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                      onChange={value =>
                        setFieldValue('crushedIcePriceTHB', value ?? '')
                      }
                      onKeyDown={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                    />
                  )}
                </Field>
                {errors.crushedIcePriceTHB && touched.crushedIcePriceTHB && (
                  <div className='text-red-500 text-xs mt-0.5'>
                    {errors.crushedIcePriceTHB}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex justify-end'>
              <Space size='small' className='gap-2'>
                <Button
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={onCancel}
                  disabled={isSubmitting || loading}
                  className='compact-button'
                >
                  Cancel
                </Button>
                <Button
                  type='primary'
                  icon={<SaveOutlined />}
                  size='small'
                  htmlType='submit'
                  loading={isSubmitting}
                  disabled={!isValid || loading}
                  className='compact-button'
                >
                  Save
                </Button>
              </Space>
            </div>
          </Form>
        )}
      </Formik>
    </div>
    </>
  )
}
